import { createTheme, MantineColorsTuple, MantineTheme, rem, TextProps } from '@mantine/core';
import { LEAGUE } from './models/NBA';

export const NBA_COLOR = "#1d9bf0";
export const WNBA_COLOR = "#ff5500";

export const nbaAccent: MantineColorsTuple = [
  "#e8f6ff",
  "#c8e8ff",
  "#95d3ff",
  "#5ebaff",
  "#31a3ff",
  "#1491ff",
  "#1d9bf0", // primary shade 6
  "#0088ea",
  "#0077cf",
  "#0062ab",
];

export const wnbaAccent: MantineColorsTuple = [
  "#fff0eb",
  "#ffd9cc",
  "#ffb399",
  "#ff8a61",
  "#ff6833",
  "#ff5f1a",
  "#ff5500", // primary shade 6
  "#e64c00",
  "#cc4400",
  "#b33b00",
];

export interface FontPreset {
  name: string;
  description: string;
  fontFamily: string;
  headingFontFamily: string;
  headingFontWeight?: string;
  fontFamilyMonospace?: string;
}

export const FONT_PRESETS = {
  proBroadcast: {
    name: 'Pro Broadcast (Barlow Semi Condensed + DM Sans)',
    description: 'High-energy sports broadcast pairing. Bold Barlow Semi Condensed headers with geometric, legible DM Sans body & numerals.',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Barlow Semi Condensed', sans-serif",
    headingFontWeight: '700',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  arenaImpact: {
    name: 'Arena Impact (Bebas Neue + DM Sans)',
    description: 'Authentic NBA arena jersey & marquee headlines (Bebas Neue) with modern geometric stats body (DM Sans).',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Bebas Neue', 'Barlow Semi Condensed', sans-serif",
    headingFontWeight: '400',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  modernGeometric: {
    name: 'Modern Geometric (Plus Jakarta Sans)',
    description: 'Contemporary sports editorial & tech. High-energy Plus Jakarta Sans sitewide with crisp numerals.',
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontWeight: '700',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  scoreboardTech: {
    name: 'Scoreboard Tech (Rajdhani + DM Sans)',
    description: 'NBA broadcast graphics vibe. High-tech Rajdhani headings paired with clean DM Sans body.',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Rajdhani', sans-serif",
    headingFontWeight: '700',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  athleticTech: {
    name: 'Athletic Tech (Barlow Semi Condensed + Inter)',
    description: 'Clean modern sports tech. Crisp Inter body & stats with Barlow Semi Condensed headers.',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Barlow Semi Condensed', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    headingFontWeight: '700',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  officialNBA: {
    name: 'Official NBA (Roboto Condensed + DM Sans)',
    description: "Inspired by NBA.com's official production typography. High-density Roboto Condensed headers & standings paired with clean DM Sans.",
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Roboto Condensed', sans-serif",
    headingFontWeight: '700',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
} as const satisfies Record<string, FontPreset>;

export type FontPresetKey = keyof typeof FONT_PRESETS;

export const ACTIVE_FONT_PRESET: FontPresetKey = 'proBroadcast';

export const getTheme = (league: LEAGUE = LEAGUE.NBA) => {
  const activeFont = FONT_PRESETS[ACTIVE_FONT_PRESET];
  return createTheme({
    fontFamily: activeFont.fontFamily,
    fontFamilyMonospace: activeFont.fontFamilyMonospace,
    headings: {
      fontWeight: activeFont.headingFontWeight || '700',
      fontFamily: activeFont.headingFontFamily,
      sizes: {
        h1: { fontSize: rem(40), lineHeight: '1.25' },
        h2: { fontSize: rem(34), lineHeight: '1.3' },
        h3: { fontSize: rem(28), lineHeight: '1.35' },
        h4: { fontSize: rem(22), lineHeight: '1.4' },
        h5: { fontSize: rem(18), lineHeight: '1.45' },
        h6: { fontSize: rem(15), lineHeight: '1.5' },
      },
    },
    black: '#161616',
    white: '#f9f9f9',
    primaryColor: 'accent',
    primaryShade: { light: 6, dark: 6 },
    colors: {
      accent: league === LEAGUE.WNBA ? wnbaAccent : nbaAccent,
    },
    components: {
      Text: {
        styles: (_theme: MantineTheme, props: TextProps) => {
          const isHeading =
            (typeof props?.fz === 'string' && props.fz.startsWith('h')) ||
            (typeof props?.size === 'string' && props.size.startsWith('h'));
          return isHeading ? { root: { fontFamily: 'var(--mantine-font-family-headings)' } } : {};
        },
      },
    },
  });
};

export const theme = getTheme(LEAGUE.NBA);